import { SetOptional } from '@appTypes/general';
import { Hierarchy, RulesReadmodelType } from '@bett3r-dev/teselly-domain';
// FE-safe subpath (jsonschema-free) — see constants.ts / resolveRulesForScope.ts.
import { normalizeOverrideGroup } from '@bett3r-dev/teselly-domain/automation/constants';
import { BackendContextType } from '@contexts/useBackend/useBackend';

/**
 * Structured error returned by `/custom-rules/dry-run` when a node in the
 * rule fails. Mirrors `CustomRulesDryRunReturnTypeSchema.error` (see TV1-1491).
 */
export type RuleDryRunStructuredError = {
  nodeId?: string;
  nodeType?: string;
  message: string;
  code?: string;
  errorType: 'transient' | 'permanent';
};

/**
 * Error thrown by `performRuleDryRun` when the backend returns a structured
 * node-level failure (HTTP 200, `dryRunResult: null`, `error: {...}`). Kept
 * exported for backwards-compatible `instanceof` checks in legacy callers
 * (TV1-1491 moved to a non-throwing return shape — callers should prefer
 * `result.error` over catching this class).
 */
export class RuleDryRunError extends Error {
  public readonly structuredError: RuleDryRunStructuredError;
  public readonly trace: unknown;

  constructor( structuredError: RuleDryRunStructuredError, trace: unknown ) {
    super( structuredError.message );
    this.name = 'RuleDryRunError';
    this.structuredError = structuredError;
    this.trace = trace;
  }
}

/**
 * Result of a dry-run call. Exactly one of `result` or `error` is populated
 * on a well-formed response; `trace` is always whatever the backend returned
 * (partial up to the failing node when `error` is set).
 */
export type RuleDryRunResult = {
  result?: number | Record<string, number>;
  trace: unknown;
  error?: RuleDryRunStructuredError;
};

/**
 * Performs a dry-run calculation of a rule to determine stock or price values
 *
 * @param params - Named parameters object
 * @param params.backend - Backend context from useBackend hook
 * @param params.ruleDefinitionId - The ID of the rule definition to use
 * @param params.declaredInputsValues - The declared inputs values for the rule
 * @param params.hierarchy - The rule hierarchy ('publication', 'item', or 'order')
 * @param params.targetEntityData - Data of the target entity (e.g., publication) for rule evaluation
 * @param params.fieldType - The type of field ('stock' or 'price')
 * @param params.targetEntityId - The ID of the target entity (e.g., publication)
 * @param params.ruleId - The ID of the saved rule instance (optional)
 * @returns `{ result, trace, error }` — `result` is set on success, `error`
 *   is set on a structured node-level failure (HTTP 200 with `dryRunResult:
 *   null`). `trace` is always whatever the backend returned (may be partial
 *   on failure). Callers should check `error` before reading `result`.
 *
 * @throws When the backend returns neither a structured `error` nor a parseable
 *   `dryRunResult` (unexpected shape — indicates a contract drift).
 */
export interface PerformRuleDryRunParams {
  ruleDefinitionId: string;
  backend: BackendContextType['backend'];
  declaredInputsValues?: Record<string, unknown>;
  hierarchy: Hierarchy;
  targetEntityId?: string;
  targetEntityData?: Record<string, unknown>;
  fieldType?: 'stock' | 'price';
  ruleId?: string;
}

export const performRuleDryRun = async ({
  ruleDefinitionId,
  backend,
  declaredInputsValues,
  hierarchy,
  targetEntityId,
  targetEntityData,
  fieldType,
  ruleId
}: PerformRuleDryRunParams ): Promise<RuleDryRunResult> => {
    const response = await backend.post.rules_dry_run({
      ruleDefinitionId,
      ruleId,
      hierarchy,
      targetEntityId,
      targetEntityData,
      declaredInputsValues
    } as any );
    const trace = ( response as any ).trace;
    const structuredError = ( response as any ).error as RuleDryRunStructuredError | undefined;

    // Structured node-level failure: return the payload so callers can render
    // the failure UX (banner + partial trace viewer) without catching an error.
    if ( structuredError ) {
      return { trace, error: structuredError };
    }

    if( response.dryRunResult ) {
      if( typeof response.dryRunResult === 'number' ) {
        return { result: response.dryRunResult, trace };
      }
      if( typeof response.dryRunResult === 'string' ) {
        return { result: parseFloat( response.dryRunResult ), trace };
      }
      if( typeof response.dryRunResult === 'object' ) {
        // Handle new backend format: { publicationVariations: { [varId]: { price: N } | { stock: N } } }
        if( response.dryRunResult ) {
          const parsed: Record<string, number> = {};
          for ( const [ varId, varData ] of Object.entries( response.dryRunResult )) {
            const data = varData as Record<string, any>;
            const val = ( fieldType ? data[fieldType] : undefined ) ?? data.stock ?? data.price;
            if( typeof val === 'number' ) {
              parsed[varId] = val;
            } else if( typeof val === 'string' ) {
              const num = parseFloat( val );
              if( !isNaN( num )) parsed[varId] = num;
            }
          }
          if( Object.keys( parsed ).length > 0 ) {
            return { result: parsed, trace };
          }
        }

        // Fallback: flat object { [key]: number | string }
        const entries = Object.entries( response.dryRunResult );
        const parsed: Record<string, number> = {};
        for ( const [ key, val ] of entries ) {
          if( typeof val === 'number' ) {
            parsed[key] = val;
          } else if( typeof val === 'string' ) {
            const num = parseFloat( val );
            if( !isNaN( num )) parsed[key] = num;
          }
        }
        if( Object.keys( parsed ).length > 0 ) {
          return { result: parsed, trace };
        }
      }
    };
    throw new RuleDryRunError(
      {
        errorType: 'permanent',
        message: `Unable to calculate ${fieldType ?? 'result'} from rule dry run.`
      },
      trace
    );
};

/**
 * Checks if rules have a rule affecting a specific field type
 *
 * @param rules - Array of rules applied
 * @param fieldType - The field type to check ('publicationStock' or 'publicationPrice')
 * @returns The first rule affecting this field, or null if none found
 */
export const getAffectingRule = (
  rules: ( RulesReadmodelType | SetOptional<RulesReadmodelType, 'hierarchyEntityIds'> )[] | undefined,
  fieldType: 'stock' | 'price'
): RulesReadmodelType | SetOptional<RulesReadmodelType, 'hierarchyEntityIds'> | null => {
  if ( !rules || rules.length === 0 ) {
    return null;
  }

  const categoryToCheck = fieldType === 'stock' ? 'publicationStock' : 'publicationPrice';

  const affectingRule = rules.find( rule =>
    rule.categories?.includes( categoryToCheck ) && rule.enabled !== false );

  return affectingRule || null;
};

// Mirror of the server-side override-group walk in
// `src/services/server/src/modules/automation/rules-utils.ts:findRulesToExecute`.
// Walks rules in the given order (caller passes most-specific-first), tracks
// claimed override groups, and annotates each rule with its effective status.
export type RuleEffectiveStatus =
  | { kind: 'active' }
  | { kind: 'overridden'; byRuleId: string; byRuleName: string; viaOverrideGroup: string };

type MinimalRule = {
  id: string;
  name?: string;
  enabled?: boolean;
  overrideGroups?: string[];
};

export type WithEffectiveStatus<T> = T & { __effectiveStatus: RuleEffectiveStatus };

// normalizeOverrideGroup is imported from `@bett3r-dev/teselly-domain` (single source
// of truth shared with the backend walk and the catalog resolver) — see rules-hierarchy.ts.

export const computeEffectiveRules = <T extends MinimalRule>( rules: T[]): WithEffectiveStatus<T>[] => {
  const claimedGroups = new Map<string, { ruleId: string; ruleName: string; originalLabel: string }>();
  return rules.map(( rule ) => {
    if ( rule.enabled === false ) {
      return { ...rule, __effectiveStatus: { kind: 'active' as const }};
    }
    const conflictRaw = ( rule.overrideGroups || []).find(( g ) => claimedGroups.has( normalizeOverrideGroup( g )));
    if ( conflictRaw ) {
      const winner = claimedGroups.get( normalizeOverrideGroup( conflictRaw ))!;
      return {
        ...rule,
        __effectiveStatus: {
          kind: 'overridden' as const,
          byRuleId: winner.ruleId,
          byRuleName: winner.ruleName,
          viaOverrideGroup: winner.originalLabel
        }
      };
    }
    ( rule.overrideGroups || []).forEach(( g ) => {
      const key = normalizeOverrideGroup( g );
      if ( !claimedGroups.has( key )) {
        claimedGroups.set( key, { ruleId: rule.id, ruleName: rule.name || rule.id, originalLabel: g });
      }
    });
    return { ...rule, __effectiveStatus: { kind: 'active' as const }};
  });
};
